import * as React from "react";
import { ResponsiveMode, Dropdown, IDropdownOption, TooltipHost, IDropdownStyles, Stack, IStackTokens, PanelType, Label, Spinner, SpinnerSize, FontIcon } from "@fluentui/react";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { INameValuePairIndex } from "../../../../../interfaces/INameValuePairIndex";
import { DatasourceService } from "../../../../../services/DatasourceService";
import { DataSourceDropdownInitializer } from "../../../../helpers/datasourceDropdownInitializer";
import { DatasourceHelper } from "../../../../helpers/dataSourceHelper";
import { DataSourcePanel } from "../../../../inputs/datasourcePanel";
import { IBaseInputProps } from "../../../../../interfaces/advancedSearch/IBaseInputProps";
import advancedSearchStateManager from "../../../../../../../advancedSearch/advancedSearchStateManager";
import { logger } from "../../../../../../../utilities/logger";

const getValueAsArray = (value: any): any[] => {
  return Array.isArray(value)
    ? value
    : (value
      ? (value as string)?.split(";")?.filter(x => x)?.map(x => x.trim() ?? [])
      : []);
}

const DatasourceMultiDropdownInput: React.FunctionComponent<IBaseInputProps> = props => {
  const languageStrings = React.useContext(LanguageContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const [datasources, setDatasources] = React.useState<IDropdownOption[]>([]);
  const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
  const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
  const [boundedField, setBoundedField] = React.useState<string>("");
  const dataSourceService = new DatasourceService();
  const dataSourceHelper = new DatasourceHelper(dataSourceService);
  const [dataSourceItem] = React.useState(
    DatasourceHelper.getInputDataSource(advancedSearchStateManager.dataSourceItems, props.displayName, props.selectedSites, props.selectedLibraries)
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    const setupAsyncWrapper = async () => {
      setIsLoading(true);
      try {
        const dropdownInitializer = new DataSourceDropdownInitializer(
          dataSourceService,
          dataSourceItem,
          props.displayName,
          true
        );
        const [result] = await Promise.all([dataSourceHelper.getSearchStructure(dataSourceItem), dropdownInitializer.initialize(setDatasources)]);
        if (Array.isArray(result)) setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
        const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, props.displayName);
        setBoundedField(boundedFieldName ?? "");
      } catch (error) {
        logger(error, "dataSourceMultipleDropdownInput.tsx (useEffect 1)");
      }
      setIsLoading(false);
    };

    setupAsyncWrapper();
  }, []);

  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      if (props.searchInputData?.value) {
        setSelectedKeys(getValueAsArray(props.searchInputData.value));
      }
    } catch (e) {
      logger(e, "dataSourceMultipleDropdownInput.tsx (useEffect 2)");
    }
  }, [props.searchInputData.value]);

  const closePanel = () => {
    advancedSearchStateManager.isDataSourcePanelActive = false;
    setIsOpen(false);
  };

  const updateSearchInputs = (index: number, value: string) => {
    DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
  };

  const openPanel = async () => {
    advancedSearchStateManager.isDataSourcePanelActive = true;
    //updateSearch(selectedKeys && selectedKeys.length === 1 ? selectedKeys[0] : "");
    setIsOpen(true);
  };

  const updateInputValue = (newValue: string) => {
    setSelectedKeys([newValue]);
    props.handleValueChange(props.searchInputData?.index, newValue);
    updateSearch(newValue);
  };

  // const HandleOnBlur = (event): void => {
  //   props.handleValueChange(props.searchInputData?.index, selectedKeys?.join(";") ?? "");
  // };

  const updateSearch = (newValue: string) => {
    const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
    updateSearchInputs(index, newValue);
  };

  const onChangeHandle = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item) {
      let selectedItems = item.selected
        ? [...selectedKeys, item.key as string]
        : selectedKeys.filter(key => key !== item.key);
      setSelectedKeys(selectedItems);
      updateSearch(selectedItems && selectedItems.length === 1 ? selectedItems[0] : "");
      props.handleValueChange(props.searchInputData?.index, selectedItems?.join(";") ?? "");
    }
  };

  const dropdownStyles: Partial<IDropdownStyles> = { dropdown: { width: "100%" } };

  const input = (
    <Dropdown
      label={props.displayName}
      selectedKeys={selectedKeys}
      placeholder={languageStrings.SelectOption}
      options={datasources}
      onChange={onChangeHandle}
      // onBlur={HandleOnBlur}
      styles={dropdownStyles}
      multiSelect
      className="xb-sp-input"
    // responsiveMode={ResponsiveMode.small}
    />
  );

  const stackTokens: IStackTokens = {
    childrenGap: 5
  };

  return (
    <>
      <Stack horizontal verticalAlign="start" tokens={stackTokens}>
        <Stack.Item grow={1}>{input}</Stack.Item>
        <Stack.Item>
          {isLoading
            ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
            : <div className="xb-search-icon-container">
              <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                <FontIcon iconName="Search" onClick={openPanel} />
              </TooltipHost>
            </div>}
        </Stack.Item>
      </Stack>
      <DataSourcePanel
        type={PanelType.customNear}
        displayName={dataSourceItem?.name}
        closePanel={closePanel}
        isOpen={isOpen}
        datasourceItem={dataSourceItem}
        datasourceService={dataSourceService}
        searchInputs={searchInputs}
        updateSearchInputs={updateSearchInputs}
        updateInputValue={updateInputValue}
        boundedField={boundedField}
      />
    </>
  );
};

export default DatasourceMultiDropdownInput;